/**
 * Het blok onder `[uit de bronnen]` uit een antwoord halen en ontleden. R57, punt B.
 *
 * **FRANKS GLASTEST VAN 0.9.108:** "wat ik NIET zie is de bron met een id per
 * agendaregel." De herkomst stond wel op elke regel van het MATERIAAL, en dat is
 * een regel in de prompt: een verzoek aan het model. gpt-oss negeerde het drie
 * keer op drie. Sinds die meting plakt de backend de dag als FEIT onder het
 * antwoord, en dit bestand haalt hem daar weer uit.
 *
 * **WAAROM GEEN MARKDOWN-TABEL, en dat is geen smaak maar een meting.**
 * `MessageBubble` rendert met `react-markdown` ZONDER `remark-gfm`, en gewone
 * CommonMark kent geen tabellen. Dat kostte op 23 augustus 2026 een dag: het
 * R40-tabelblok kwam als een doorlopende regel van 883 tekens in beeld. Die les
 * hoeft niet twee keer, dus dit blok is geen tabel maar een reeks regels met een
 * eigen scheidingsteken.
 *
 * **HET IS EEN PURE FUNCTIE**, net als `tabelblok.js`, zodat de beslissing op
 * UITKOMST te toetsen is in plaats van op de vraag of er toevallig iets in de
 * DOM staat.
 */

// Moet gelijk blijven aan `MERK` in backend/services/dagprogramma.py.
// Er is een wachter die dat afdwingt; zie agendablok.test.js.
//
// **HET NOEMT MET OPZET GEEN BRON MEER. R57, gebrek 3 van 27 augustus 2026.**
// Dit merkteken staat boven ELK blok, ook boven een blok uit Gmail of uit
// Drive. Zolang er `[uit de agenda]` stond, las Frank het woord agenda boven
// vier mails. De bronnen staan nu in de HERKOMST, die de backend erachter
// zet en die hieronder wordt gerenderd.
export const MERK = "\n\n---\n[uit de bronnen]";

// Moet gelijk blijven aan `VELDSCHEIDING` in dezelfde module. De backend haalt
// dit teken uit de veldwaarden weg voordat hij de rij schrijft, dus een rij
// draagt er per constructie precies drie.
export const VELDSCHEIDING = " :: ";

// wanneer, titel, extras, bron. Vier, en de backend legt datzelfde getal vast.
const VELDEN = 4;

/** Ontleedt het blok NA het merkteken. Geeft null als het er niet uitziet zoals
 *  wij het schrijven; dan valt de aanroeper terug op platte tekst. */
export function ontleed(blok) {
  const regels = (blok || "").split("\n");
  // De eerste regel draagt de dag en welke agenda het was; hij komt van dezelfde
  // code die de rijen schrijft, dus hij hoeft niet ontleed te worden.
  const herkomst = (regels[0] || "").trim() || null;
  const rijen = regels
    .slice(1)
    .filter((r) => r.includes(VELDSCHEIDING))
    .map((r) => r.split(VELDSCHEIDING).map((v) => v.trim()))
    // EEN RIJ MET EEN ANDER AANTAL VELDEN IS ONZE RIJ NIET. Hem toch tonen zou
    // betekenen dat de tijd op de plek van de titel belandt, en dat is erger
    // dan terugvallen op platte tekst.
    .filter((v) => v.length === VELDEN)
    .map(([wanneer, titel, extras, bron]) => ({
      wanneer, titel, extras, bron, ...mailUit(bron),
    }));
  if (!rijen.length) return null;
  return { herkomst, rijen };
}

// Hoe de bron van een MAILregel eruitziet: `Gmail, id 199a...`. Moet gelijk
// blijven aan `google_bronnen.MAIL.naam` en aan de vorm die `_regel` in
// `dagprogramma.py` schrijft; er staat aan beide kanten een wachter op.
const MAILBRON = /^Gmail,\s*id\s+(\S+)$/;

/**
 * De bericht-id uit een bronveld, als het een mailregel is. R57 fase 3.
 *
 * **DIT IS WAT HET ANTWOORDEN BEREIKBAAR MAAKT.** Zonder deze afleiding staat
 * er wel een antwoordscherm in de code en is er geen weg ernaartoe - precies
 * het gebrek dat dit project keer op keer tegenkomt: de belofte staat er, het
 * vermogen niet.
 *
 * **HIJ IS STRENG MET OPZET.** Alleen een bron die letterlijk met `Gmail,` begint
 * krijgt een antwoordknop. Een ruimere match zou een agenda-afspraak een knop
 * geven die naar Gmail wijst, en dat is een knop die niets kan doen.
 */
export function mailUit(bron) {
  const m = MAILBRON.exec((bron || "").trim());
  return m ? { mailId: m[1] } : {};
}

/**
 * Splitst een bericht in het gewone antwoord en het agendablok.
 * Geen merkteken of onleesbaar blok: alles blijft tekst, precies zoals het was.
 */
export function splits(content) {
  const heel = content || "";
  const i = heel.indexOf(MERK);
  if (i === -1) return { tekst: heel, agenda: null };
  const agenda = ontleed(heel.slice(i + MERK.length));
  if (!agenda) return { tekst: heel, agenda: null };
  return { tekst: heel.slice(0, i), agenda };
}

/**
 * GL211-5 (glasronde 0.9.211, Frank 28 oktober 2026): de webpagina's van een bronregel als lijst.
 *
 * De backend schrijft `bron :: Webpagina: <adres>, <adres> :: 14 gevonden :: Webpagina`; bij online
 * onderzoek stond dat als een lap tekst. Per adres het domein als naam, en alleen http(s), zodat er
 * nooit een link naar iets anders komt. Een domein dat vaker voorkomt krijgt een volgnummer. PUUR.
 */
export function webpaginas(rij) {
  if (!rij || (rij.bron || "").trim() !== "Webpagina") return null;
  const lijst = (rij.titel || "").replace(/^Webpagina:\s*/, "").split(/,\s+/);
  const geteld = {};
  const uit = [];
  for (const adres of lijst) {
    let domein;
    try {
      const u = new URL(adres.trim());
      if (u.protocol !== "https:" && u.protocol !== "http:") continue;
      domein = u.hostname.replace(/^www\./, "");
    } catch {
      continue;
    }
    geteld[domein] = (geteld[domein] || 0) + 1;
    uit.push({ url: adres.trim(), naam: geteld[domein] > 1 ? `${domein} (${geteld[domein]})` : domein });
  }
  return uit.length ? uit : null;
}
